import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import AnimatedBackground from '../components/AnimatedBackground';
import { useCountUp } from '../hooks/useScrollAnimation';
import { images } from '../data/images';
import { CheckCircle2, ArrowRight } from 'lucide-react';

const ApproachPage = () => {
  const { t } = useLanguage();

  // Count up hooks for statistics
  const [complianceRef, complianceCount] = useCountUp(100, 2000);
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-[#1E2A38] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: `url(${images.approach.process})` }}
        />
        <AnimatedBackground className="opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1E2A38]/50 to-[#1E2A38]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <ScrollReveal>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('approach.title')}
            </h1>
            <p className="text-white/70 text-lg max-w-3xl leading-relaxed">
              {t('approach.intro')}
            </p>
            <div className="w-24 h-1 bg-[#0066CC] rounded-full mt-8" />
          </ScrollReveal>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Process Timeline */}
          <div className="relative">
            {/* Connection Line - Desktop */}
            <div className="hidden lg:block absolute top-24 left-[12.5%] right-[12.5%] h-0.5 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#0066CC]/30 to-transparent" />
              {/* Animated line */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#0066CC] to-transparent animate-shimmer" />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {t('approach.methodology').map((step, index) => (
                <ScrollReveal key={index} delay={index * 150}>
                  <div className="relative group">
                    {/* Step Card */}
                    <div className="bg-[#F8F9FA] rounded-2xl p-8 hover:bg-[#2B3A4D] transition-all duration-500 group-hover:shadow-2xl h-full group-hover:-translate-y-2">
                      {/* Step Number */}
                      <div className="relative mb-6">
                        <span className="text-6xl font-bold text-[#0066CC]/10 group-hover:text-[#0066CC]/30 transition-colors duration-300">
                          {step.step}
                        </span>
                        
                        {/* Connection dot */}
                        <div className="hidden lg:block absolute -top-2 left-1/2 transform -translate-x-1/2">
                          <div className="w-4 h-4 rounded-full bg-white border-4 border-[#0066CC] group-hover:scale-125 transition-transform duration-300" />
                        </div>
                      </div>
                      
                      {/* Content */}
                      <h3 className="text-xl font-semibold text-[#2B3A4D] group-hover:text-white mb-3 transition-colors duration-300">
                        {step.title}
                      </h3>
                      <p className="text-[#6B7B8C] group-hover:text-white/70 transition-colors duration-300">
                        {step.description}
                      </p>
                      
                      {/* Arrow indicator */}
                      {index < 3 && (
                        <div className="hidden lg:flex absolute top-24 -right-4 z-10">
                          <ArrowRight className="w-6 h-6 text-[#0066CC]/50 group-hover:text-[#0066CC] group-hover:translate-x-1 transition-all duration-300" />
                        </div>
                      )}
                    </div>
                    
                    {/* Active Indicator */}
                    <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-8 h-1 rounded-full bg-[#0066CC] opacity-0 group-hover:opacity-100 transition-all duration-300" />
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Principles Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Visual */}
            <ScrollReveal direction="right" className="order-2 lg:order-1">
              <div className="relative aspect-square max-w-md mx-auto">
                {/* Background Image */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden">
                  <img 
                    src={images.patterns.geometric}
                    alt="Methodology"
                    className="w-full h-full object-cover opacity-20"
                    loading="lazy"
                  />
                </div>
                
                {/* Animated rings */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-full h-full">
                    {/* Outer ring */}
                    <div className="absolute inset-4 rounded-full border border-[#2B3A4D]/10 animate-spin-slow" style={{ animationDuration: '30s' }}>
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-[#0066CC]" />
                    </div>
                    
                    {/* Middle ring */}
                    <div className="absolute inset-12 rounded-full border border-[#0066CC]/20">
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 rounded-full bg-[#0066CC]/60" />
                    </div>
                    
                    {/* Inner ring */}
                    <div className="absolute inset-20 rounded-full border-2 border-[#0066CC]/30 animate-pulse" style={{ animationDuration: '3s' }} />
                    
                    {/* Center element */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-24 h-24 rounded-2xl bg-[#2B3A4D] flex items-center justify-center shadow-2xl transform hover:rotate-12 transition-transform duration-500">
                        <div className="w-12 h-12 rounded-lg bg-[#0066CC]" />
                      </div>
                    </div>
                    
                    {/* Floating nodes */}
                    <div className="absolute top-8 right-1/4 w-4 h-4 rounded-full bg-[#0066CC] animate-float" style={{ animationDelay: '0s' }} />
                    <div className="absolute bottom-12 left-1/4 w-3 h-3 rounded-full bg-[#0066CC]/60 animate-float" style={{ animationDelay: '1s' }} />
                    <div className="absolute top-1/3 right-8 w-2 h-2 rounded-full bg-[#0066CC]/40 animate-float" style={{ animationDelay: '2s' }} />
                  </div>
                </div>
                
                {/* Connection lines */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
                  <line x1="50" y1="10" x2="50" y2="35" stroke="#0066CC" strokeWidth="0.3" strokeDasharray="2,2" opacity="0.5" />
                  <line x1="50" y1="65" x2="50" y2="90" stroke="#0066CC" strokeWidth="0.3" strokeDasharray="2,2" opacity="0.5" />
                  <line x1="10" y1="50" x2="35" y2="50" stroke="#0066CC" strokeWidth="0.3" strokeDasharray="2,2" opacity="0.5" />
                  <line x1="65" y1="50" x2="90" y2="50" stroke="#0066CC" strokeWidth="0.3" strokeDasharray="2,2" opacity="0.5" />
                </svg>
              </div>
            </ScrollReveal>
            
            {/* Content */}
            <ScrollReveal direction="left" className="order-1 lg:order-2">
              <div>
                <h2 className="text-3xl font-bold text-[#2B3A4D] mb-8">
                  {t('approach.principles.title')}
                </h2>
                <ul className="space-y-6">
                  {t('approach.principles.items').map((principle, index) => (
                    <li key={index} className="flex items-start gap-4 group">
                      <div className="w-8 h-8 rounded-lg bg-[#0066CC]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0066CC] transition-all duration-300 group-hover:scale-110">
                        <CheckCircle2 className="w-5 h-5 text-[#0066CC] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <span className="text-[#5C5C5C] text-lg pt-0.5 group-hover:text-[#2B3A4D] transition-colors duration-200">{principle}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Enterprise Standards Banner */}
      <section className="py-16 bg-[#2B3A4D] relative overflow-hidden">
        <AnimatedBackground className="opacity-20" />
        
        <ScrollReveal>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Enterprise Standards</h3>
                <p className="text-white/70">Built on proven methodologies for long-term success</p>
              </div>
              <div className="flex items-center gap-8 md:gap-12">
                <div className="text-center group">
                  <p 
                    ref={complianceRef}
                    className="text-3xl md:text-4xl font-bold text-[#0066CC] group-hover:scale-110 transition-transform duration-300"
                  >
                    {complianceCount}%
                  </p>
                  <p className="text-white/60 text-sm mt-1">Compliance Focus</p>
                </div>
                <div className="w-px h-12 bg-white/20" />
                <div className="text-center group">
                  <p className="text-3xl md:text-4xl font-bold text-[#0066CC] group-hover:scale-110 transition-transform duration-300">ISO</p>
                  <p className="text-white/60 text-sm mt-1">Aligned Standards</p>
                </div>
                <div className="w-px h-12 bg-white/20" />
                <div className="text-center group">
                  <p className="text-3xl md:text-4xl font-bold text-[#0066CC] group-hover:scale-110 transition-transform duration-300">EU</p>
                  <p className="text-white/60 text-sm mt-1">Regulations Ready</p>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
};

export default ApproachPage;
